import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import {
  Link2,
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  Plus,
  Trash2,
} from 'lucide-react'
import {
  agentsAdminApiClient,
  agentsApiClient,
  triggersApiClient,
} from '@/lib/api'
import { pathFor, ROUTE_REGISTRY } from '@/lib/routes'
import { SectionTabs } from '@/pages/platform/platform-shared'
import { useAppAuth } from '@/contexts/app-auth.context'
import { EmptyState } from '@/components/ui/empty-state'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { confirmDialog } from '@/lib/utils/window-utils'
import {
  isBuiltInTriggerEvent,
  type AgentWithVoiceSettings,
  type Trigger,
} from '@super/shared-types'

export function TriggersPage() {
  useDocumentHead({
    title: 'Triggers',
    description: 'Define custom triggers that start outbound agents',
  })

  const navigate = useNavigate()
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [triggers, setTriggers] = useState<Trigger[]>([])
  const [agents, setAgents] = useState<AgentWithVoiceSettings[]>([])
  const [loading, setLoading] = useState(true)

  const load = async () => {
    // Company resolves asynchronously on refresh; stay in the loading state until
    // it's ready so we don't flash the empty state before the first fetch.
    if (!currentCompany?.id) {
      return
    }
    setLoading(true)
    try {
      // Super admins viewing a specific company need the company-scoped admin
      // endpoint (mirrors AgentsListPage): the bare getCompanyAgents() resolves
      // the session's home company instead of the active one, which makes the
      // agentTriggers join silently empty.
      const agentsPromise = isSuperAdmin
        ? agentsAdminApiClient.getAgentsForCompany(currentCompany.id)
        : agentsApiClient.getCompanyAgents()
      const definitionsPromise = isSuperAdmin
        ? triggersApiClient.getDefinitionsForCompanyByAdmin(currentCompany.id)
        : triggersApiClient.getDefinitions()
      const [triggerList, agentList] = await Promise.all([
        definitionsPromise,
        agentsPromise,
      ])
      setTriggers(triggerList)
      setAgents(Array.isArray(agentList) ? agentList : [])
    } catch {
      toast.error('Failed to load triggers')
      setTriggers([])
      setAgents([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [currentCompany?.id])

  // Map trigger eventName → first agent whose AgentTrigger references it.
  // Today's data model is 1:1 (one agent per trigger event); revisit when that changes.
  const agentByEventName = new Map<string, AgentWithVoiceSettings>()
  for (const agent of agents) {
    for (const at of agent.agentTriggers ?? []) {
      if (at.eventName && !agentByEventName.has(at.eventName)) {
        agentByEventName.set(at.eventName, agent)
      }
    }
  }

  const handleCreateNew = () => navigate(ROUTE_REGISTRY.TRIGGERS_NEW.path)
  const handleEdit = (trigger: Trigger) =>
    navigate(pathFor('TRIGGERS_DETAIL', { id: trigger.id }))

  const handleDelete = async (trigger: Trigger) => {
    if (!confirmDialog(`Delete trigger "${trigger.name}"?`)) return
    if (!currentCompany?.id) return
    const ok = isSuperAdmin
      ? await triggersApiClient.deleteTriggerForCompany(
          trigger.id,
          currentCompany.id,
        )
      : await triggersApiClient.deleteTrigger(trigger.id)
    if (ok) {
      toast.success(`Trigger "${trigger.name}" deleted`)
      setTriggers(prev => prev.filter(item => item.id !== trigger.id))
    } else {
      toast.error('Failed to delete trigger')
    }
  }

  const handleToggleActive = async (trigger: Trigger) => {
    if (!currentCompany?.id) return
    const updated = isSuperAdmin
      ? await triggersApiClient.updateTriggerForCompany(
          trigger.id,
          currentCompany.id,
          { active: !trigger.active },
        )
      : await triggersApiClient.updateTrigger(trigger.id, {
          active: !trigger.active,
        })
    if (updated) {
      toast.success(
        `Trigger "${updated.name}" ${updated.active ? 'activated' : 'deactivated'}`,
      )
      setTriggers(prev =>
        prev.map(item => (item.id === updated.id ? updated : item)),
      )
    } else {
      toast.error('Failed to update trigger')
    }
  }

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 pb-24">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-6">
          <PageHeader title="Triggers" />
          <Button
            onClick={handleCreateNew}
            variant="form"
            size="sm"
            className="h-9 w-fit shrink-0"
          >
            Create trigger
          </Button>
        </div>
        <SectionTabs
          tabs={[
            {
              label: 'Configuration',
              to: ROUTE_REGISTRY.TRIGGERS.path,
              active: true,
            },
            {
              label: 'Events',
              to: ROUTE_REGISTRY.TRIGGERS_EVENTS.path,
              active: false,
            },
          ]}
        />

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : triggers.length === 0 ? (
          <EmptyState
            icon={<Play className="h-8 w-8 text-muted-foreground" />}
            title="No triggers yet"
            description="Create a trigger to start an outbound agent from a webhook (e.g. lease renewal, move-in, maintenance)."
            actionLabel="Create your first trigger"
            onAction={handleCreateNew}
            actionButtonVariant="default"
          />
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden md:table-cell">
                    Linked agent
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Variables
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="w-[100px]" align="center">
                    Options
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {triggers.map(trigger => {
                  const linkedAgent = agentByEventName.get(trigger.eventName)
                  return (
                    <TableRow
                      key={trigger.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleEdit(trigger)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{trigger.name}</span>
                          {isBuiltInTriggerEvent(trigger.eventName) && (
                            <Badge variant="outline" className="text-xs">
                              Built-in
                            </Badge>
                          )}
                        </div>
                        {trigger.description && (
                          <div className="text-xs text-muted-foreground truncate max-w-[280px]">
                            {trigger.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {linkedAgent ? (
                          <Link
                            to={pathFor('AGENTS_DETAIL', {
                              id: linkedAgent.id,
                            })}
                            className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline"
                            onClick={event => event.stopPropagation()}
                          >
                            <Link2 className="h-4 w-4 text-muted-foreground" />
                            {linkedAgent.name}
                          </Link>
                        ) : trigger.active ? (
                          // Land directly in the outbound agent form with the
                          // trigger pre-selected — no intermediate create modal.
                          <Link
                            to={`/agents/new?type=OUTBOUND&triggerEventName=${encodeURIComponent(trigger.eventName)}`}
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                            onClick={event => event.stopPropagation()}
                          >
                            <Plus className="h-4 w-4" />
                            Create agent
                          </Link>
                        ) : null}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">
                        {trigger.variables.length}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge
                          variant={trigger.active ? 'default' : 'secondary'}
                          className={
                            trigger.active
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs'
                              : 'bg-muted text-muted-foreground text-xs'
                          }
                        >
                          {trigger.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={event => event.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={event => {
                                event.stopPropagation()
                                handleEdit(trigger)
                              }}
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit trigger
                            </DropdownMenuItem>
                            {!isBuiltInTriggerEvent(trigger.eventName) && (
                              <>
                                <DropdownMenuItem
                                  onClick={event => {
                                    event.stopPropagation()
                                    handleToggleActive(trigger)
                                  }}
                                >
                                  {trigger.active ? (
                                    <>
                                      <Pause className="w-4 h-4 mr-2" />
                                      Deactivate trigger
                                    </>
                                  ) : (
                                    <>
                                      <Play className="w-4 h-4 mr-2" />
                                      Activate trigger
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={event => {
                                    event.stopPropagation()
                                    handleDelete(trigger)
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete trigger
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}
